import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Phone, Building2, Clock, CheckCircle, XCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: {
    label: 'Under Review',
    color: 'text-yellow-400',
    bg: 'bg-yellow-400/10 border-yellow-400/30',
    icon: Clock,
    description: 'Aapki application review mein hai. Jaldi hi update milega.',
  },
  approved: {
    label: 'Approved',
    color: 'text-emerald-400',
    bg: 'bg-emerald-400/10 border-emerald-400/30',
    icon: CheckCircle,
    description: 'Badhai ho! Aapki application approve ho gayi hai.',
  },
  rejected: {
    label: 'Rejected',
    color: 'text-red-400',
    bg: 'bg-red-400/10 border-red-400/30',
    icon: XCircle,
    description: 'Maaf kijiye, aapki application is baar approve nahi ho payi.',
  },
};

const DealerStatusPage = () => {
  const [mobile, setMobile] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    const cleaned = mobile.replace(/\D/g, '');
    if (cleaned.length < 10) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const res = await fetch(`${BACKEND_URL}/api/dealer/status/${cleaned}`);
      const data = await res.json();
      setResult(data);
    } catch {
      setError('Server se connect nahi ho pa raha. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (ts) => {
    try {
      return new Date(ts).toLocaleDateString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric',
      });
    } catch {
      return '-';
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] pt-28 pb-20 px-4" data-testid="dealer-status-page">
      <SEOHead
        title="Dealer Application Status"
        description="Check your Avi Cement dealer application status using your registered mobile number. Track approval progress in real-time."
        path="/dealer-status"
        noindex
      />
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl mx-auto text-center mb-12"
      >
        <span className="text-[#E85A1C] text-xs font-semibold tracking-[0.2em] uppercase">
          Dealer Status Tracker
        </span>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-3 mb-4">
          Application ka <span className="text-[#F5A623]">Status</span> dekhein
        </h1>
        <p className="text-gray-400 text-sm sm:text-base max-w-md mx-auto">
          Apna registered mobile number daalein aur apni dealer application ka status turant jaanein.
        </p>
      </motion.div>

      {/* Search Form */}
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15 }}
        onSubmit={handleSearch}
        className="max-w-lg mx-auto mb-10"
        data-testid="dealer-status-form"
      >
        <div className="relative flex items-center">
          <Phone className="absolute left-4 w-5 h-5 text-gray-500" />
          <input
            type="tel"
            data-testid="dealer-status-mobile-input"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            placeholder="Mobile number daalein (e.g. 9876543210)"
            className="w-full pl-12 pr-32 py-4 rounded-xl bg-[#111113] border border-gray-800 text-white placeholder-gray-500 focus:outline-none focus:border-[#E85A1C] transition-colors text-base"
            maxLength={12}
          />
          <button
            type="submit"
            data-testid="dealer-status-search-btn"
            disabled={loading}
            className="absolute right-2 px-5 py-2.5 rounded-lg bg-[#E85A1C] hover:bg-[#d14e15] text-white font-medium text-sm transition-all flex items-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <Search className="w-4 h-4" />
                Search
              </>
            )}
          </button>
        </div>
        {error && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-red-400 text-sm mt-3 flex items-center gap-1.5"
            data-testid="dealer-status-error"
          >
            <AlertCircle className="w-4 h-4" /> {error}
          </motion.p>
        )}
      </motion.form>

      {/* Results */}
      <AnimatePresence mode="wait">
        {result && (
          <motion.div
            key="results"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            className="max-w-2xl mx-auto"
            data-testid="dealer-status-results"
          >
            {!result.found ? (
              <div className="text-center py-12 bg-[#111113] rounded-2xl border border-gray-800">
                <AlertCircle className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <h3 className="text-white text-lg font-medium mb-2" data-testid="dealer-status-not-found">
                  Koi Application Nahi Mili
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  Is mobile number se koi dealer application register nahi hai.
                </p>
                <Link
                  to="/dealer-form"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#E85A1C] hover:bg-[#d14e15] text-white rounded-lg font-medium text-sm transition-all"
                  data-testid="dealer-status-apply-link"
                >
                  Apply Now <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-400 text-sm mb-2">
                  {result.count} application{result.count > 1 ? 's' : ''} mili:
                </p>
                {result.applications.map((app, idx) => {
                  const cfg = statusConfig[app.status] || statusConfig.pending;
                  const StatusIcon = cfg.icon;
                  return (
                    <motion.div
                      key={app.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className={`p-6 rounded-2xl border ${cfg.bg} backdrop-blur-sm`}
                      data-testid={`dealer-status-card-${idx}`}
                    >
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-3">
                            <div className={`p-2 rounded-lg ${cfg.bg}`}>
                              <StatusIcon className={`w-5 h-5 ${cfg.color}`} />
                            </div>
                            <div>
                              <span className={`text-sm font-semibold ${cfg.color}`} data-testid={`dealer-status-label-${idx}`}>
                                {cfg.label}
                              </span>
                              <p className="text-gray-400 text-xs">{cfg.description}</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-gray-500 shrink-0" />
                              <span className="text-white text-sm truncate" data-testid={`dealer-status-company-${idx}`}>
                                {app.company_name}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-gray-500 shrink-0" />
                              <span className="text-gray-300 text-sm">
                                Applied: {formatDate(app.timestamp)}
                              </span>
                            </div>
                          </div>

                          <div className="mt-2 text-gray-500 text-xs">
                            {app.district}, {app.state} &middot; {app.name}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DealerStatusPage;
